import axiosNest from '../../api/axiosNest';
import { toErrorMessage } from '../userOffers/orderActionsApi';

/**
 * Rozliczenie zlecenia — klient API (`apps/api/src/settlement/`, prace
 * 26.10.2026, pakiet P4b; przełącznik `orderSettlement`, S-26.10-12). Liczby,
 * opisy i powody blokad przychodzą z API — front nie liczy postoju sam.
 */

/** Klucze przełączników — dane z rejestru API, nie przemianowujemy. */
export const FEATURE_ORDER_SETTLEMENT = 'orderSettlement';
export const FEATURE_ADMIN_DASHBOARD_SECTIONS = 'adminDashboardSections';

/** Najkrótszy powód — jak w API (`settlement.ts`). */
export const MIN_REASON_LENGTH = 10;
export const MAX_REASON_LENGTH = 500;

export type SettlementListState = 'open' | 'invoicing';

export interface SettlementRow {
  id: number;
  number: string | null;
  client: string | null;
  finishedAt: string;
  place: string | null;
  status: SettlementListState;
  statusLabel: string;
  stopPending: boolean;
  stopDecision: string | null;
  stopAmountEur: number | null;
  stopMeasured: number;
  stopMissing: number;
}

export interface SettlementList {
  state: SettlementListState;
  total: number;
  page: number;
  perPage: number;
  windowDays: number;
  rows: SettlementRow[];
}

export interface SettlementStopPoint {
  pointOrder: number;
  kind: string | null;
  label: string;
  measured: boolean;
  arrivedAt: string | null;
  departedAt: string | null;
  hours: number | null;
  paidHours: number | null;
  missingReason: string | null;
}

export interface SettlementLine {
  kind: 'freight' | 'stop' | 'correction';
  label: string;
  source: string;
  amount: number | null;
  currency: string | null;
}

export interface SettlementDetail {
  order: {
    id: number;
    number: string | null;
    client: string | null;
    relation: string;
    fromDate: string;
    toDate: string;
    tmsOrderStatus: string | null;
    planningStatus: string;
    vehicle: string | null;
    carrier: string | null;
  };
  status: SettlementListState;
  statusLabel: string;
  stop: {
    points: SettlementStopPoint[];
    measuredCount: number;
    missingCount: number;
    freeHours: number;
    rateEur: number;
    paidHours: number | null;
    amountEur: number | null;
    decisionRequired: boolean;
    description: string;
  };
  stopDecision: {
    decision: string;
    label: string;
    reason: string | null;
    amountEur: number | null;
    paidHours: number | null;
    freeHours: number | null;
    rateEur: number | null;
    by: string | null;
    at: string | null;
  } | null;
  invoicing: { at: string; by: string | null; note: string | null } | null;
  lines: SettlementLine[];
  totals: Array<{ currency: string; amount: number }>;
  incomplete: boolean;
  corrections: Array<{ id: number; amount: number; currency: string; reason: string; by: string | null; at: string }>;
  avizations: Array<{ id: number; channel: string | null; result: string | null; at: string; by: string | null }>;
  history: Array<{ id: number; text: string | null; reason: string | null; at: string; by: string | null }>;
  defaultCurrency: string;
  /** Powód blokady czynności; `null` = wolno. */
  actions: {
    charge: string | null;
    waive: string | null;
    correction: string | null;
    invoicing: string | null;
  };
}

export async function fetchSettlements(params: {
  state: SettlementListState;
  q?: string;
  page?: number;
}): Promise<SettlementList> {
  const { data } = await axiosNest.get<SettlementList>('/settlements', { params });
  return data;
}

export async function fetchSettlement(id: number): Promise<SettlementDetail> {
  const { data } = await axiosNest.get<SettlementDetail>(`/settlements/${id}`);
  return data;
}

export async function decideStop(
  id: number,
  decision: 'charge' | 'waive',
  reason?: string,
): Promise<SettlementDetail> {
  const { data } = await axiosNest.post<SettlementDetail>(`/settlements/${id}/stop-decision`, {
    decision,
    ...(reason ? { reason } : {}),
  });
  return data;
}

export async function addCorrection(
  id: number,
  body: { amount: number; currency: string; reason: string },
): Promise<SettlementDetail> {
  const { data } = await axiosNest.post<SettlementDetail>(`/settlements/${id}/corrections`, body);
  return data;
}

export async function handToInvoicing(id: number, note?: string): Promise<SettlementDetail> {
  const { data } = await axiosNest.post<SettlementDetail>(`/settlements/${id}/invoicing`, note ? { note } : {});
  return data;
}

/** Kwota z walutą po polsku: 1 240,00 EUR. */
export function money(amount: number | null, currency: string | null): string {
  if (amount === null || currency === null) return '—';
  return `${amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

/** Godziny jako „3 h 20 min” — jak w API. */
export function hoursText(h: number | null): string {
  if (h === null) return '—';
  const total = Math.round(h * 60);
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  if (hh === 0) return `${mm} min`;
  return mm === 0 ? `${hh} h` : `${hh} h ${mm} min`;
}

/** Kwota korekty z pola formularza: „-20”, „-20,50” → liczba; inaczej `null`. */
export function parseAmountInput(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^-?\d+(\.\d{1,2})?$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) && n !== 0 ? n : null;
}

export { toErrorMessage };

// ── Pulpit Administracji: sekcje (S-26.10-11) ────────────────────────────

export interface TmsWriteRow {
  orderId: number;
  orderNumber: string | null;
  customerOrderNumber: string | null;
  client: string | null;
  preOrderId: number | null;
  userOfferId: number | null;
  viaWriteStatus: string;
  tmsNumber: string | null;
  tmsConfirmedAt: string | null;
  files: number;
  attempts: number;
  failedAttempts: number;
  outcome: 'saved' | 'sent' | 'failed' | 'pending' | 'other';
  retried: boolean;
  lastError: string | null;
  lastChangeAt: string | null;
}

export interface SoldRow {
  userOfferId: number;
  number: string | null;
  client: string | null;
  carrier: string | null;
  vehicle: string | null;
  soldAt: string | null;
  clientPrice: number | null;
  clientCurrency: string | null;
  cost: { amount: number; currency: string } | null;
  costSource: string | null;
  marginPln: number | null;
  marginPct: number | null;
  marginInOrderCurrency: { amount: number; currency: string } | null;
  band: string | null;
  bandRule: string | null;
  breakdown: string[];
  missing: string[];
}

export interface AdminDashboardSectionsData {
  tmsWrites: TmsWriteRow[];
  sold: SoldRow[];
  soldWindowDays: number;
  computedAt: string;
}

export async function fetchAdminDashboardSections(): Promise<AdminDashboardSectionsData> {
  const { data } = await axiosNest.get<AdminDashboardSectionsData>('/intake/dashboard/administration');
  return data;
}
